import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { BulletList, BulletListItem, Button, Callout, Column, CustomModal, Row, Spinner, Typography } from 'seagull';
import { Checkmark, Info } from 'seagull/icons/essentials';
import { getTestIds } from 'seagull/utils/TestIds';
import { useLocaleKeys } from '~/i18n';
import { CountEmphasis } from '../CountEmphasis/CountEmphasis';
import styles from './BulkSendResultsModal.module.scss';
import { clientlessFailureCount, failedHeadline, isRunCancelled, namedFailures, noClientHeadline, failedRowDetail, modalTitle, summaryLine, unreachedDraftsLine } from './BulkSendResultsModal.utils';
import { SentProposalsTable } from './SentProposalsTable';
import { useBulkSendResults } from './useBulkSendResults';
export const GetBulkSendResultsModalTID = (parentTestId)=>getTestIds(parentTestId);
export const BulkSendResultsModal = (param)=>{
    let { batchId, jobName, onClose, testId } = param;
    var _ref;
    const { t: drawerTexts } = useLocaleKeys('navigation.tasksDrawer');
    const texts = drawerTexts.bulkSendProposals;
    const { BulkSendResultsModalTestIds } = useMemo(()=>GetBulkSendResultsModalTID(testId), [
        testId
    ]);
    const { results, isLoading } = useBulkSendResults(batchId, texts.modalLoadFailed(), BulkSendResultsModalTestIds.ErrorToast());
    return /*#__PURE__*/ _jsx(CustomModal, {
        testId: BulkSendResultsModalTestIds.Root(),
        width: "min(860px, 94vw)",
        maxWidth: 860,
        disableFooterPadding: true,
        onClose: onClose,
        bi: {
            closeButton: {
                name: 'bulk_send_results_close'
            }
        },
        header: /*#__PURE__*/ _jsx(Row, {
            justifyContent: "center",
            children: /*#__PURE__*/ _jsx(Typography, {
                variant: "h6",
                weight: "normal",
                testId: BulkSendResultsModalTestIds.Title(),
                children: modalTitle(jobName, texts)
            })
        }),
        footer: /*#__PURE__*/ _jsx(Row, {
            justifyContent: "center",
            className: styles.footer,
            children: /*#__PURE__*/ _jsx(Button, {
                bi: {
                    name: 'bulk_send_results_close_footer'
                },
                testId: BulkSendResultsModalTestIds.CloseButton(),
                onClick: onClose,
                children: texts.modalClose()
            })
        }),
        children: /*#__PURE__*/ _jsxs(Column, {
            gap: 16,
            className: styles.content,
            children: [
                isLoading && /*#__PURE__*/ _jsx(Spinner, {
                    centeredInBox: true,
                    centerBoxHeight: "auto",
                    testId: BulkSendResultsModalTestIds.Loader()
                }),
                results && /*#__PURE__*/ _jsxs(Row, {
                    gap: 10,
                    alignItems: "start",
                    children: [
                        isRunCancelled(results.status) ? /*#__PURE__*/ _jsx(Info, {
                            size: 20,
                            className: styles.cancelledIcon
                        }) : /*#__PURE__*/ _jsx(Checkmark, {
                            size: 20,
                            className: styles.checkIcon
                        }),
                        /*#__PURE__*/ _jsxs(Column, {
                            gap: 2,
                            children: [
                                /*#__PURE__*/ _jsx(Typography, {
                                    weight: isRunCancelled(results.status) ? 'normal' : undefined,
                                    testId: BulkSendResultsModalTestIds.Summary(),
                                    children: isRunCancelled(results.status) ? summaryLine(results.status, texts) : /*#__PURE__*/ _jsx(CountEmphasis, {
                                        text: summaryLine(results.status, texts),
                                        count: results.status.sent,
                                        testId: BulkSendResultsModalTestIds.Summary()
                                    })
                                }),
                                isRunCancelled(results.status) && /*#__PURE__*/ _jsx(Typography, {
                                    variant: "paragraph2",
                                    skin: "secondary",
                                    testId: BulkSendResultsModalTestIds.CancelledDetail(),
                                    children: unreachedDraftsLine(results.status, texts)
                                })
                            ]
                        })
                    ]
                }),
                results && results.status.failedDrafts.length > 0 && /*#__PURE__*/ _jsx(Callout, {
                    intent: "error",
                    iconSize: "small",
                    iconAlignment: "firstLine",
                    testId: BulkSendResultsModalTestIds.Failures(),
                    children: /*#__PURE__*/ _jsxs(Column, {
                        gap: 4,
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                children: /*#__PURE__*/ _jsx(CountEmphasis, {
                                    text: failedHeadline(results.status.failedDrafts.length, texts),
                                    count: results.status.failedDrafts.length,
                                    testId: BulkSendResultsModalTestIds.Failures()
                                })
                            }),
                            /*#__PURE__*/ _jsx(BulletList, {
                                spacing: "compact",
                                children: namedFailures(results.status).map((draft)=>/*#__PURE__*/ _jsxs(BulletListItem, {
                                        variant: "paragraph3",
                                        bulletSize: "small",
                                        testId: BulkSendResultsModalTestIds.FailedRow(draft.draftId),
                                        children: [
                                            /*#__PURE__*/ _jsx(Typography, {
                                                as: "span",
                                                variant: "paragraph3",
                                                weight: "bold",
                                                children: draft.customerName
                                            }),
                                            ' ',
                                            failedRowDetail(draft, texts)
                                        ]
                                    }, draft.draftId))
                            }),
                            clientlessFailureCount(results.status) > 0 && /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph3",
                                testId: BulkSendResultsModalTestIds.NoClientGroup(),
                                children: /*#__PURE__*/ _jsx(CountEmphasis, {
                                    text: noClientHeadline(clientlessFailureCount(results.status), texts),
                                    count: clientlessFailureCount(results.status),
                                    testId: BulkSendResultsModalTestIds.NoClientGroup()
                                })
                            })
                        ]
                    })
                }),
                !isLoading && /*#__PURE__*/ _jsx(Column, {
                    className: styles.table,
                    children: /*#__PURE__*/ _jsx(SentProposalsTable, {
                        proposals: (_ref = results === null || results === void 0 ? void 0 : results.sent) !== null && _ref !== void 0 ? _ref : [],
                        testId: BulkSendResultsModalTestIds.Table()
                    })
                })
            ]
        })
    });
};
